import React, { useState, useEffect } from 'react';

import MomentUtils from '@date-io/moment';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import Field from '../../Fields/Field';
import MultiSelectDropdown from '../../Fields/MultiSelectDropdown';
import ActionButton from '../../Common/ActionButton';
import InputTextField from '../../Fields/InputTextField';
import InputSelectField from '../../Fields/InputSelectField';
import useReport from '../../../Hooks/useReport';
import InvoiceReports from '../../../Utils/invoiceReports'
import NoticeBlock from '../../Common/NoticeBlock';
import MessageList from '../../Common/MessageList';
import featureFlags from '../../../featureFlags';
import { validateFilters } from '../../../Validators/InvoiceReportsForm';

import './InvoiceReportsForm.scss';

const reportTypeOptions = featureFlags.INVOICE_REPORT_CSV_3 ? [
  { label: "Select", value: "" },
  { label: "Invoice Report", value: "INVOICE_REPORT" },
  { label: "Invoice Monthly Detail Report", value: "DETAIL_REPORT" },
  { label: "Validation & Other Detail Report", value: "VALIDATION_REPORT" },
  { label: "Adjustment Report", value: "ADJUSTMENT_REPORT" },
  { label: "Carryover Invoice Report", value: "CARRYOVER_REPORT" },
  { label: "Summary by Rate Report", value: "RATE_REPORT" },
  { label: "Summary by Rate Amount Report", value: "RATE_AMOUNT_REPORT" },
] : featureFlags.INVOICE_REPORT_CSV_2 ? [
  { label: "Select", value: "" },
  { label: "Invoice Report", value: "INVOICE_REPORT" },
  { label: "Invoice Monthly Detail Report", value: "DETAIL_REPORT" },
  { label: "Validation & Other Detail Report", value: "VALIDATION_REPORT" },
  { label: "Adjustment Report", value: "ADJUSTMENT_REPORT" },
  { label: "Carryover Invoice Report", value: "CARRYOVER_REPORT" },
] : [
  { label: "Select", value: "" },
  { label: "Invoice Report", value: "INVOICE_REPORT" },
  { label: "Invoice Monthly Detail Report", value: "DETAIL_REPORT" },
  { label: "Validation & Other Detail Report", value: "VALIDATION_REPORT" }
];

const rateTypeBaseOptions = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Validation & Other Types", value: "VALIDATION_OTHER" },
];

const rateTypeValidationOptions = [
  { label: "Prepaid Validation", value: "PREPAID_VALIDATION" },
  { label: "Bill Back Validation", value: "BILL_BACK_VALIDATION" },
  { label: "Hotel Storage", value: "HOTEL_STORAGE" },
  { label: "Office/Retail Rent", value: "OFFICE_RETAIL_RENT" },
  { label: "Miscellaneous", value: "MISCELLANEOUS" },
];
const validationOptionValues = rateTypeValidationOptions.map((option) => option.value);

const rateTypeBaseValidationOptions = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Validation & Other Types", value: "VALIDATION_OTHER" },
  { label: "Prepaid Validation", value: "PREPAID_VALIDATION" },
  { label: "Bill Back Validation", value: "BILL_BACK_VALIDATION" },
  { label: "Hotel Storage", value: "HOTEL_STORAGE" },
  { label: "Office/Retail Rent", value: "OFFICE_RETAIL_RENT" },
  { label: "Miscellaneous", value: "MISCELLANEOUS" },
];

const rateTypeFeeOptions = [
  { label: "Monthly Fee", value: "MONTHLY_FEE" },
  { label: "Activation Fee", value: "ACTIVATION_FEE" },
  { label: "Access Device Fee", value: "ACCESS_DEVICE_FEE" },
  { label: "Credit Card Fee", value: "CREDIT_CARD_FEE" },
  { label: "Late Fee", value: "LATE_FEE" },
  { label: "Lost Device Fee", value: "LOST_DEVICE_FEE" },
  { label: "Mail Invoice Fee", value: "MAIL_INVOICE_FEE" },
];

const rateTypeOptions = (reportTypeSelection = '') => {
  switch (reportTypeSelection) {
    case "INVOICE_REPORT":
      return rateTypeBaseOptions;
    case "DETAIL_REPORT":
      return rateTypeFeeOptions;
    case "VALIDATION_REPORT":
      return rateTypeValidationOptions;
    case "CARRYOVER_REPORT":
    case "ADJUSTMENT_REPORT":
      return rateTypeBaseValidationOptions
    default:
      return [];
  }
};

const monthOptions = [
  { label: "Select", value: null },
  { label: "January", value: 0 },
  { label: "February", value: 1 },
  { label: "March", value: 2 },
  { label: "April", value: 3 },
  { label: "May", value: 4 },
  { label: "June", value: 5 },
  { label: "July", value: 6 },
  { label: "August", value: 7 },
  { label: "September", value: 8 },
  { label: "October", value: 9 },
  { label: "November", value: 10 },
  { label: "December", value: 11 },
];

const yearOptions = () => {
  const startYear = 2021;
  const endYear = new Date().getFullYear() + 1;
  let yearOptions = [{ label: 'Select', value: null }];
  for (let i = startYear; i <= endYear; i++) {
    yearOptions.push({ label: i, value: i });
  };
  return yearOptions;
}

const rateTypesValueRenderer = (selected, _options) => {
  let generateItemsSelectedText = () => {
    //when using default options ignore "Validation & Other Types"
    const thisSelected = (_options.length === 7) ? selected.filter(({ label }) => {
      return label !== "Validation & Other Types";
    }) : selected;
    return (thisSelected.length === 1) ? thisSelected[0].label : thisSelected.length + " items selected"
  }

  return selected.length === _options.length ? "All selected" :
    !selected.length ? "None selected" :
      generateItemsSelectedText();
};

function InvoiceReportsForm({ facilityId }) {
  const {
    filterValues,
    downloadCSVReport,
    downloadPDFReport,
    inputOnChange,
    inputOnDropDownChange,
    resetFilters,
    isDownloadingCSV,
    isDownloadingPDF,
    errors,
    setErrors
  } = useReport(InvoiceReports, facilityId);

  const validateFilterValues = (reportFormat) => {
    try {
      validateFilters(filterValues);
      if (reportFormat === 'CSV') {
        downloadCSVReport(true);
      } else {
        downloadPDFReport(true);
      }
    } catch (error) {
      setErrors([error.message]);
    }
  }

  const [selectedReportType, setSelectedReportType] = useState('');
  const [selectedRateTypes, setSelectedRateTypes] = useState([]);
  const [userSelectedRateTypes, setUserSelectedRateTypes] = useState([]);
  const [isValidationOtherSelected, setIsValidationOtherSelected] = useState(false);
  const [parkingPeriodStartMonth, setParkingPeriodStartMonth] = useState(null);
  const [parkingPeriodStartYear, setParkingPeriodStartYear] = useState(null);
  const [parkingPeriodEndMonth, setParkingPeriodEndMonth] = useState(null);
  const [parkingPeriodEndYear, setParkingPeriodEndYear] = useState(null);

  const rateTypesIncludeAllValidationOptions = ['CARRYOVER_REPORT', 'ADJUSTMENT_REPORT'].includes(selectedReportType);

  /**
   * Select all validation subtypes when VALIDATION_OTHER is selected
   * Deselect all validation subtypes when VALIDATION_OTHER is deselected
   */
  const handleValidationTypesHeaderToggle = (updatedIsValidationOtherSelected) => {
    const updatedSelectedRateTypes = userSelectedRateTypes.filter(({ value }) => !validationOptionValues.includes(value));

    if (updatedIsValidationOtherSelected) {
      updatedSelectedRateTypes.push(...rateTypeValidationOptions);
    }

    return updatedSelectedRateTypes;
  }

  /**
   * Deselect validation_other when at least one subtype is deselected
   * Select validation_other when all subtypes are selected
   */
  const handleValidationSubtypesToggle = (updatedIsValidationOtherSelected) => {
    let updatedSelectedRateTypes = userSelectedRateTypes;

    const selectedValues = userSelectedRateTypes.map(({ value }) => value);
    const allValidationOptionsSelected = validationOptionValues.every(value => selectedValues.includes(value));

    if (!allValidationOptionsSelected && updatedIsValidationOtherSelected) {
      updatedSelectedRateTypes = userSelectedRateTypes.filter(({ value }) => value !== 'VALIDATION_OTHER');
      setIsValidationOtherSelected(false);
    } else if (allValidationOptionsSelected && !updatedIsValidationOtherSelected) {
      updatedSelectedRateTypes.push({ label: "Validation & Other Types", value: "VALIDATION_OTHER" });
      setIsValidationOtherSelected(true);
    }

    return updatedSelectedRateTypes;
  }

  useEffect(() => {
    let updatedSelectedRateTypes = userSelectedRateTypes;

    const updatedIsValidationOtherSelected = userSelectedRateTypes.some(({ value }) => value === 'VALIDATION_OTHER');
    const validationOtherToggled = updatedIsValidationOtherSelected !== isValidationOtherSelected;

    // Calculations to determine validation header and subtype selections
    if (rateTypesIncludeAllValidationOptions) {
      if (validationOtherToggled) {
        updatedSelectedRateTypes = handleValidationTypesHeaderToggle(updatedIsValidationOtherSelected);
        setIsValidationOtherSelected(updatedIsValidationOtherSelected);
      } else {
        updatedSelectedRateTypes = handleValidationSubtypesToggle(updatedIsValidationOtherSelected);
      }
    }

    setSelectedRateTypes(updatedSelectedRateTypes);
    inputOnDropDownChange("rateTypes", updatedSelectedRateTypes);
  }, [userSelectedRateTypes]);

  useEffect(() => {
    const allRateOptions = rateTypeOptions(selectedReportType);
    setUserSelectedRateTypes(allRateOptions);

    inputOnDropDownChange('reportType', selectedReportType);
  }, [selectedReportType]);

  useEffect(() => {
    inputOnDropDownChange('parkingPeriodStartMonth', parkingPeriodStartMonth);
  }, [parkingPeriodStartMonth]);

  useEffect(() => {
    inputOnDropDownChange('parkingPeriodStartYear', parkingPeriodStartYear);
  }, [parkingPeriodStartYear]);

  useEffect(() => {
    inputOnDropDownChange('parkingPeriodEndMonth', parkingPeriodEndMonth);
  }, [parkingPeriodEndMonth]);

  useEffect(() => {
    inputOnDropDownChange('parkingPeriodEndYear', parkingPeriodEndYear);
  }, [parkingPeriodEndYear]);

  const onReset = () => {
    setSelectedReportType("");
    setParkingPeriodStartMonth(null);
    setParkingPeriodStartYear(null);
    setParkingPeriodEndMonth(null);
    setParkingPeriodEndYear(null);
    resetFilters();
  }

  const isRateTypeDisabled =
    !selectedReportType ||
    selectedReportType === "RATE_REPORT" ||
    selectedReportType === "RATE_AMOUNT_REPORT";

  const rateTypeLabel =
    selectedReportType === "DETAIL_REPORT" ? "Monthly Fees" :
    selectedReportType === "VALIDATION_REPORT" ? "Validation & Other Types" : "Rate Type";

  const isPDFDownloadButtonDisabled =
    selectedReportType !== "INVOICE_REPORT" &&
    selectedReportType !== "DETAIL_REPORT";

  const isParkingPeriodDisabled = selectedReportType === "CARRYOVER_REPORT";

  return (
    <div className="parkchirp--invoice-reports--filter-form-container">
      <div className="parkchirp--invoice-reports--filter-form">
        <h2>Invoice Reports</h2>

        <div className="parkchirp--invoice-reports--filter-inputs">

          <div className={"parkchirp--invoice-reports--filter-input-block " + (rateTypesIncludeAllValidationOptions ? "has-indented-validation-options" : "")}>
            <Field label="Report Type"
              id="parkchirp--invoice-reports-filter--report-type"
              layout="columns">
              <InputSelectField
                inputId="parkchirp--invoice-reports-filter--report-type--select-field"
                options={reportTypeOptions}
                value={selectedReportType}
                onChange={setSelectedReportType}
                editMode={true}
              />
            </Field>
            <Field label={rateTypeLabel}
              id="parkchirp--invoice-reports-filter--rate-type"
              layout="columns">
              <MultiSelectDropdown
                id={"parkchirp--invoice-reports-filter--rate-type--multi-select-dropdown"}
                label="Rate Type"
                labelWidth={"sm"}
                options={rateTypeOptions(selectedReportType)}
                selected={selectedRateTypes}
                onChange={setUserSelectedRateTypes}
                disabledMode={isRateTypeDisabled}
                hasSelectAll={true}
                valueRenderer={rateTypesValueRenderer}
              />
            </Field>
          </div>

          <div className="parkchirp--invoice-reports--filter-input-block">
            <Field label="Invoice Start Date"
              id="parkchirp--invoice-reports-filter--invoice-start-date"
              layout="columns">
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <KeyboardDatePicker
                  id="parkchirp--invoice-reports-filter--invoice-start-date"
                  clearable
                  variant="outlined"
                  value={filterValues.invoiceStartDate}
                  placeholder=""
                  onChange={inputOnChange('invoiceStartDate')}
                  format="MM/DD/yyyy"
                />
              </MuiPickersUtilsProvider>
            </Field>
            <Field label="Invoice End Date"
              id="parkchirp--invoice-reports-filter--invoice-end-date"
              layout="columns">
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <KeyboardDatePicker
                  id="parkchirp--invoice-reports-filter--invoice-end-date"
                  clearable
                  variant="outlined"
                  value={filterValues.invoiceEndDate}
                  placeholder=""
                  onChange={inputOnChange('invoiceEndDate')}
                  format="MM/DD/yyyy"
                />
              </MuiPickersUtilsProvider>
            </Field>
          </div>

          <div className="parkchirp--invoice-reports--filter-input-block has-two-inputs">
            <Field label="Parking Period Start"
              id="parkchirp--invoice-reports-filter--report-type"
              layout="columns">
              <InputSelectField
                inputId="parkchirp--invoice-reports-filter--parking-period-start-month--select-field"
                options={monthOptions}
                value={parkingPeriodStartMonth}
                onChange={setParkingPeriodStartMonth}
                disabledMode={isParkingPeriodDisabled}
                editMode={true}
              />
              <InputSelectField
                inputId="parkchirp--invoice-reports-filter--parking-period-start-year--select-field"
                options={yearOptions()}
                value={parkingPeriodStartYear}
                onChange={setParkingPeriodStartYear}
                disabledMode={isParkingPeriodDisabled}
                editMode={true}
              />
            </Field>
            <Field label="Parking Period End"
              id="parkchirp--invoice-reports-filter--report-type"
              layout="columns">
              <InputSelectField
                inputId="parkchirp--invoice-reports-filter--parking-period-end-month--select-field"
                options={monthOptions}
                value={parkingPeriodEndMonth}
                onChange={setParkingPeriodEndMonth}
                disabledMode={isParkingPeriodDisabled}
                editMode={true}
              />
              <InputSelectField
                inputId="parkchirp--invoice-reports-filter--parking-period-end-year--select-field"
                options={yearOptions()}
                value={parkingPeriodEndYear}
                onChange={setParkingPeriodEndYear}
                disabledMode={isParkingPeriodDisabled}
                editMode={true}
              />
            </Field>
          </div>

          <div className="parkchirp--invoice-reports--filter-input-block">
            <Field label="Sub. ID"
              id="parkchirp--invoice-reports-filter--payor-id"
              layout="columns">
              <InputTextField
                inputId="parkchirp--invoice-reports-filter--payor-id"
                value={filterValues.payorId}
                onChange={inputOnChange('payorId')}
                editMode={true}
              />
            </Field>
            <Field label="Sub. Name"
              id="parkchirp--invoice-reports-filter--billing-name"
              layout="columns">
              <InputTextField
                inputId="parkchirp--invoice-reports-filter--billing-name"
                value={filterValues.payorBillingName}
                onChange={inputOnChange('payorBillingName')}
                editMode={true}
              />
            </Field>
          </div>
        </div>

        <div className="parkchirp--invoice-reports--filter-actions">
          <ActionButton label="Download CSV"
            classNames={["button", "button-primary parkchirp--invoice-reports--filter-action download-csv"]}
            onClick={() => { validateFilterValues('CSV')} }
            inProgressState={isDownloadingCSV}
            disabled={!selectedReportType}
          />

          {featureFlags.INVOICE_REPORTPDF &&
            <ActionButton label="Download PDF"
              classNames={["button", "button-primary parkchirp--invoice-reports--filter-action download-pdf"]}
              onClick={() => { validateFilterValues('PDF')} }
              inProgressState={isDownloadingPDF}
              disabled={isPDFDownloadButtonDisabled}
            />
          }

          <button type="button"
            className="button parkchirp--invoice-reports--filter-action"
            onClick={onReset}
          >
            Reset
          </button>
        </div>
        {!!errors.length &&
          <div className="parkchirp--invoice-reports--filter-errors">
            <NoticeBlock type="error" containerClasses={['parkchirp--sidebar-block']}>
              <MessageList
                heading={`An error occurred while attempting to generate the report.`}
                messages={errors}
              />
            </NoticeBlock>
          </div>
        }
      </div>
    </div>
  )
}

export default React.memo(InvoiceReportsForm);

//for testing
export { rateTypesValueRenderer };